import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { isValidVideoSrc } from "@/libs/utils";

interface LazyLoadVideoProps
  extends React.VideoHTMLAttributes<HTMLVideoElement> {
  keepAlive?: boolean; // 한번 보인다음에 다시 소스를 로드하는게 아니라 계속 유지할지 여부입니다. 모바일 게임클립에서 좌우 스크롤시 좀 하다보면 영상재생이 안되어서 추가 했습니다.
}

const mimeTypes: Record<string, string> = {
  mp4: "video/mp4",
  webm: "video/webm",
  ogg: "video/ogg",
};

const getMimeType = (src: string): string => {
  const extension = src.split(".").pop()?.toLowerCase(); // 확장자 추출
  return mimeTypes[extension || ""] || "video/mp4"; // 기본값: 'video/mp4'
};

export const LazyLoadVideo = forwardRef<HTMLVideoElement, LazyLoadVideoProps>(
  (
    {
      src,
      muted = true,
      loop = false,
      controls = false,
      autoPlay,
      keepAlive = false,
      ...rest
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement, []);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
            } else {
              if (keepAlive) return;
              setIsVisible(false);
            }
          });
        },
        { threshold: 0.1 },
      );

      if (videoRef.current instanceof HTMLVideoElement) {
        const currentVideo = videoRef.current;
        observer.observe(currentVideo);

        return () => {
          observer.unobserve(currentVideo);
        };
      }
    }, [keepAlive]);

    return (
      <video
        ref={videoRef}
        muted={muted}
        loop={loop}
        controls={controls}
        autoPlay={isVisible && autoPlay} // 보일 때만 autoplay
        playsInline
        {...rest}
      >
        {isVisible && typeof src === "string" && isValidVideoSrc(src) && (
          <source src={src} type={getMimeType(src)} />
        )}
        Your browser does not support the video tag.
      </video>
    );
  },
);

LazyLoadVideo.displayName = "LazyLoadVideo";
